import React, { createContext, useContext, useEffect, useState, PropsWithChildren } from 'react';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase, supabaseStorageKey, Session, User } from '../services/supabase';
import { securityErrorToMetadata, securityMonitorService } from '../services/securityMonitorService';
import { clearRememberedCredentials } from '../utils/rememberCredentials';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (identifier: string, password: string) => Promise<Session | null>;
  signUp: (data: {
    email?: string;
    phone?: string;
    password: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
  }) => Promise<Session | null>;
  /** Claim an existing auth user by phone/email + password (no new sign-up). */
  claimExistingUser: (data: {
    email?: string;
    phone?: string;
    password: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
  }) => Promise<Session | null>;
  sendPasswordReset: (email: string) => Promise<void>;
  sendPasswordOtp: (phone: string) => Promise<string>;
  verifyPasswordOtp: (phone: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const LOG_REFRESH_TOKENS =
  process.env.REACT_APP_LOG_AUTH_TOKEN_RESPONSE === 'true' ||
  process.env.REACT_APP_LOG_REFRESH_TOKENS === 'true' ||
  process.env.EXPO_PUBLIC_LOG_AUTH_TOKEN_RESPONSE === 'true' ||
  process.env.EXPO_PUBLIC_LOG_REFRESH_TOKENS === 'true' ||
  process.env.EXPO_PUBLIC_DEBUG_TOKENS === 'true';

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const clearCorruptedSession = async (reason?: string) => {
    if (reason) {
      // eslint-disable-next-line no-console
      console.warn(`⚠️ Clearing local auth session (${reason})`);
    }
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('⚠️ Supabase local signOut failed; removing storage directly', err);
    }
    if (supabaseStorageKey) {
      localStorage.removeItem(supabaseStorageKey);
    }
    setSession(null);
    setUser(null);
  };

  const isFatalAuthError = (error: any): boolean => {
    if (!error) return false;
    const status = Number(error?.status ?? error?.code ?? error?.statusCode ?? 0);
    const message = (
      error?.message ||
      error?.error ||
      error?.error_description ||
      ''
    )
      .toString()
      .toLowerCase();

    const fatalFragments = [
      'invalid refresh token',
      'refresh token not found',
      'refresh token revoked',
      'invalid_grant',
      'session not found',
    ];

    if (fatalFragments.some(fragment => message.includes(fragment))) {
      return true;
    }

    if (status === 400 || status === 401) {
      return true;
    }

    return false;
  };

  const sanitizeStoredSession = () => {
    if (!supabaseStorageKey) return;
    try {
      const raw = localStorage.getItem(supabaseStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const refreshToken =
        parsed?.refresh_token ??
        parsed?.currentSession?.refresh_token ??
        parsed?.user?.refresh_token;
      if (!refreshToken || typeof refreshToken !== 'string') {
        localStorage.removeItem(supabaseStorageKey);
      }
    } catch {
      localStorage.removeItem(supabaseStorageKey);
    }
  };

  const logSessionRefreshToken = (label: string, activeSession?: Session | null) => {
    if (!LOG_REFRESH_TOKENS) return;
    const rt = activeSession?.refresh_token;
    if (typeof rt === 'string') {
      // eslint-disable-next-line no-console
      console.log(`[auth-debug] ${label}: session.refresh_token len=${rt.length}`, rt);
    } else {
      // eslint-disable-next-line no-console
      console.log(`[auth-debug] ${label}: session.refresh_token missing`);
    }
  };

  const logClientSecurityEvent = (
    payload: Parameters<typeof securityMonitorService.logClientSecurityEvent>[0]
  ) => {
    void securityMonitorService.logClientSecurityEvent(payload);
  };

  // Initial session check
  useEffect(() => {
    const getSession = async () => {
      sanitizeStoredSession();
      const { data, error } = await supabase.auth.getSession();
      if (error && isFatalAuthError(error)) {
        await clearCorruptedSession(error?.message || 'fatal auth error');
        setLoading(false);
        return;
      }
      const activeSession = data.session;
      setSession(activeSession);
      setUser(activeSession?.user ?? null);
      setLoading(false);
    };
    getSession();

    // Subscribe to auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if ((event as string) === 'TOKEN_REFRESH_FAILED') {
        await clearCorruptedSession('TOKEN_REFRESH_FAILED');
        return;
      }
      if ((event as string) === 'SIGNED_IN' || (event as string) === 'TOKEN_REFRESHED') {
        logSessionRefreshToken(event as string, newSession);
      }
      setSession(newSession);
      setUser(newSession?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Normalize to E.164-ish: accept "+1XXXXXXXXXX" or bare 10-digit US numbers, keep leading +
  const normalizePhone = (value: string) => {
    const cleaned = value.replace(/[^\d+]/g, '');
    if (!cleaned) return null;
    // If user entered 10 US digits, prefix +1 so it matches DB values like +1XXXXXXXXXX
    const asDigits = cleaned.replace(/\D/g, '');
    const candidate = cleaned.startsWith('+')
      ? cleaned
      : asDigits.length === 10
        ? `+1${asDigits}`
        : `+${asDigits}`;
    const isValid = /^\+[1-9]\d{6,14}$/.test(candidate);
    return isValid ? candidate : null;
  };
  const signIn = async (identifier: string, password: string) => {
    setLoading(true);
    const trimmedIdentifier = identifier.trim();
    const normalizedPhone = normalizePhone(trimmedIdentifier);
    const isEmail = trimmedIdentifier.includes('@');
    const isPhone = !isEmail && !!normalizedPhone;
    const normalizedIdentifierForLog = isPhone ? normalizedPhone : trimmedIdentifier.toLowerCase();

    if (!isEmail && !isPhone) {
      setLoading(false);
      logClientSecurityEvent({
        eventFamily: 'auth',
        eventType: 'sign_in',
        severity: 'warning',
        outcome: 'blocked',
        sourceName: 'web.auth.sign_in',
        identifier: trimmedIdentifier,
        metadata: { reason: 'invalid_identifier' },
      });
      throw new Error('Enter a valid phone number (e.g. 3217894561).');
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword(
        isPhone ? { phone: normalizedPhone as string, password } : { email: trimmedIdentifier, password }
      );
      if (error) {
        logClientSecurityEvent({
          eventFamily: 'auth',
          eventType: 'sign_in',
          severity: 'warning',
          outcome: 'failure',
          sourceName: 'web.auth.sign_in',
          identifier: normalizedIdentifierForLog,
          metadata: {
            method: isPhone ? 'phone' : 'email',
            error: securityErrorToMetadata(error),
          },
        });
        throw error;
      }
      logClientSecurityEvent({
        eventFamily: 'auth',
        eventType: 'sign_in',
        severity: 'info',
        outcome: 'success',
        sourceName: 'web.auth.sign_in',
        identifier: normalizedIdentifierForLog,
        metadata: {
          method: isPhone ? 'phone' : 'email',
          userId: data.user?.id ?? null,
        },
      });
      return data?.session ?? null;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (payload: {
    email?: string;
    phone?: string;
    password: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
  }) => {
    const isUserAlreadyRegistered = (err: unknown) => {
      const message = (err as { message?: string }).message?.toLowerCase() ?? '';
      const code = (err as { code?: string }).code?.toLowerCase() ?? '';
      if (code === 'weak_password' || message.includes('weak') || message.includes('easy to guess')) {
        return false;
      }
      return (
        ['user_already_exists', 'email_exists', 'phone_exists', 'user_exists'].includes(code) ||
        message.includes('already registered') ||
        message.includes('already exists')
      );
    };

    setLoading(true);
    const trimmedEmail = payload.email?.trim() || '';
    const trimmedPhone = payload.phone?.trim() || '';
    const normalizedPhone = trimmedPhone ? normalizePhone(trimmedPhone) : null;
    const isEmail = trimmedEmail.includes('@');
    const isPhone = !!normalizedPhone;

    if (!isEmail && !isPhone) {
      setLoading(false);
      throw new Error('Enter a valid phone number (e.g. 32178984561).');
    }

    const fullName =
      payload.fullName?.trim() ||
      `${payload.firstName ?? ''} ${payload.lastName ?? ''}`.trim() ||
      trimmedEmail ||
      trimmedPhone ||
      'User';
    const options = {
      data: {
        first_name: payload.firstName?.trim() || undefined,
        last_name: payload.lastName?.trim() || undefined,
        full_name: fullName,
        phone: normalizedPhone || undefined,
        email: trimmedEmail || undefined,
      },
    };
    const { data: signUpData, error } = await supabase.auth.signUp(
      isPhone
        ? { phone: normalizedPhone as string, password: payload.password, options }
        : { email: trimmedEmail, password: payload.password, options }
    );
    if (error) {
      const errCode = (error as { code?: string }).code;
      const errMsg = (error as { message?: string }).message;
      if (process.env.NODE_ENV === 'development') {
        console.info('[signUp] Supabase error:', { code: errCode, message: errMsg });
      }
      if (isUserAlreadyRegistered(error)) {
        const { data: claimData, error: claimError } = await supabase.functions.invoke(
          'claim-existing-user',
          {
            body: {
              email: trimmedEmail || undefined,
              phone: normalizedPhone || undefined,
              password: payload.password,
              first_name: payload.firstName?.trim() || undefined,
              last_name: payload.lastName?.trim() || undefined,
              full_name: fullName,
            },
          }
        );

        if (claimError) {
          setLoading(false);
          let message = claimError.message;
          if (claimError instanceof FunctionsHttpError && claimError.context?.json) {
            try {
              const body = await claimError.context.json() as { error?: string };
              if (body?.error) message = body.error;
            } catch {
              // ignore JSON parse failure, use default message
            }
          }
          if (message.includes('User not found')) {
            throw new Error('This number is already registered. Please sign in instead.');
          }
          throw new Error(message);
        }
        if (claimData && (claimData as { error?: string }).error) {
          const msg = (claimData as { error: string }).error;
          setLoading(false);
          if (msg.includes('User not found')) {
            throw new Error('This number is already registered. Please sign in instead.');
          }
          throw new Error(msg);
        }

        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword(
          isPhone ? { phone: normalizedPhone as string, password: payload.password } : { email: trimmedEmail, password: payload.password }
        );
        if (signInError) {
          setLoading(false);
          throw signInError;
        }
        setLoading(false);
        return signInData?.session ?? null;
      }
      setLoading(false);
      throw error;
    }
    // If the user signed up with phone but provided an email, attach it to auth.
    if (isPhone && trimmedEmail && signUpData?.session) {
      try {
        const { error: emailError } = await supabase.auth.updateUser({ email: trimmedEmail });
        if (emailError) {
          // Non-fatal; user can still proceed, but email reset won't work until this is set.
          console.warn('Unable to attach email to auth user:', emailError);
        }
      } catch (err) {
        console.warn('Unable to attach email to auth user:', err);
      }
    }

    setLoading(false);
    return signUpData?.session ?? null;
  };

  const claimExistingUser = async (payload: {
    email?: string;
    phone?: string;
    password: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
  }) => {
    setLoading(true);
    const trimmedEmail = payload.email?.trim() || '';
    const trimmedPhone = payload.phone?.trim() || '';
    const normalizedPhone = trimmedPhone ? normalizePhone(trimmedPhone) : null;
    const isPhone = !!normalizedPhone;
    const fullName =
      payload.fullName?.trim() ||
      `${payload.firstName ?? ''} ${payload.lastName ?? ''}`.trim() ||
      trimmedEmail ||
      trimmedPhone ||
      'User';
    try {
      const { data: claimData, error: claimError } = await supabase.functions.invoke(
        'claim-existing-user',
        {
          body: {
            email: trimmedEmail || undefined,
            phone: normalizedPhone || undefined,
            password: payload.password,
            first_name: payload.firstName?.trim() || undefined,
            last_name: payload.lastName?.trim() || undefined,
            full_name: fullName,
          },
        }
      );
      if (claimError) {
        let message = claimError.message;
        if (claimError instanceof FunctionsHttpError && claimError.context?.json) {
          try {
            const body = await claimError.context.json() as { error?: string };
            if (body?.error) message = body.error;
          } catch {
            // ignore
          }
        }
        setLoading(false);
        if (message.includes('User not found')) {
          throw new Error('This number is already registered. Please sign in instead.');
        }
        throw new Error(message);
      }
      if (claimData && (claimData as { error?: string }).error) {
        const msg = (claimData as { error: string }).error;
        setLoading(false);
        if (msg.includes('User not found')) {
          throw new Error('This number is already registered. Please sign in instead.');
        }
        throw new Error(msg);
      }
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword(
        isPhone ? { phone: normalizedPhone as string, password: payload.password } : { email: trimmedEmail, password: payload.password }
      );
      if (signInError) {
        setLoading(false);
        throw signInError;
      }
      setLoading(false);
      return signInData?.session ?? null;
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const sendPasswordReset = async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      logClientSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'password_reset_email',
        severity: 'warning',
        outcome: 'blocked',
        sourceName: 'web.account_recovery.password_reset_email',
        identifier: trimmed,
        metadata: { reason: 'invalid_email' },
      });
      throw new Error('Enter a valid email address to reset your password.');
    }
    const redirectTo = `${window.location.origin}/reset-password`;
    const normalizedEmail = trimmed.toLowerCase();
    const { error } = await supabase.auth.resetPasswordForEmail(trimmed, { redirectTo });
    if (error) {
      logClientSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'password_reset_email',
        severity: 'warning',
        outcome: 'failure',
        sourceName: 'web.account_recovery.password_reset_email',
        identifier: normalizedEmail,
        metadata: {
          error: securityErrorToMetadata(error),
        },
      });
      throw error;
    }
    logClientSecurityEvent({
      eventFamily: 'account_recovery',
      eventType: 'password_reset_email',
      severity: 'info',
      outcome: 'success',
      sourceName: 'web.account_recovery.password_reset_email',
      identifier: normalizedEmail,
    });
  };

  const sendPasswordOtp = async (phone: string) => {
    const normalized = normalizePhone(phone.trim());
    if (!normalized) {
      logClientSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'password_reset_otp_send',
        severity: 'warning',
        outcome: 'blocked',
        sourceName: 'web.account_recovery.password_reset_otp_send',
        identifier: phone.trim(),
        metadata: { reason: 'invalid_phone' },
      });
      throw new Error('Enter a valid phone number (e.g. 3217894561).');
    }
    const { error } = await supabase.auth.signInWithOtp({
      phone: normalized,
      options: { shouldCreateUser: false },
    });
    if (error) {
      logClientSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'password_reset_otp_send',
        severity: 'warning',
        outcome: 'failure',
        sourceName: 'web.account_recovery.password_reset_otp_send',
        identifier: normalized,
        metadata: {
          error: securityErrorToMetadata(error),
        },
      });
      throw error;
    }
    logClientSecurityEvent({
      eventFamily: 'account_recovery',
      eventType: 'password_reset_otp_send',
      severity: 'info',
      outcome: 'success',
      sourceName: 'web.account_recovery.password_reset_otp_send',
      identifier: normalized,
    });
    return normalized;
  };

  const verifyPasswordOtp = async (phone: string, token: string) => {
    const trimmedToken = token.trim();
    if (!trimmedToken || trimmedToken.length < 4) {
      logClientSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'password_reset_otp_verify',
        severity: 'warning',
        outcome: 'blocked',
        sourceName: 'web.account_recovery.password_reset_otp_verify',
        identifier: phone.trim(),
        metadata: { reason: 'invalid_token_length' },
      });
      throw new Error('Enter the verification code.');
    }
    const normalized = normalizePhone(phone.trim());
    if (!normalized) {
      logClientSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'password_reset_otp_verify',
        severity: 'warning',
        outcome: 'blocked',
        sourceName: 'web.account_recovery.password_reset_otp_verify',
        identifier: phone.trim(),
        metadata: { reason: 'invalid_phone' },
      });
      throw new Error('Enter a valid phone number (e.g. 3217894561).');
    }
    const { error, data } = await supabase.auth.verifyOtp({
      phone: normalized,
      token: trimmedToken,
      type: 'sms',
    });
    if (error) {
      logClientSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'password_reset_otp_verify',
        severity: 'warning',
        outcome: 'failure',
        sourceName: 'web.account_recovery.password_reset_otp_verify',
        identifier: normalized,
        metadata: {
          error: securityErrorToMetadata(error),
        },
      });
      throw error;
    }
    if (!data?.session) {
      logClientSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'password_reset_otp_verify',
        severity: 'warning',
        outcome: 'failure',
        sourceName: 'web.account_recovery.password_reset_otp_verify',
        identifier: normalized,
        metadata: { reason: 'missing_session' },
      });
      throw new Error('Unable to verify the code. Please try again.');
    }
    logClientSecurityEvent({
      eventFamily: 'account_recovery',
      eventType: 'password_reset_otp_verify',
      severity: 'info',
      outcome: 'success',
      sourceName: 'web.account_recovery.password_reset_otp_verify',
      identifier: normalized,
      metadata: {
        userId: data.user?.id ?? null,
      },
    });
  };

  const signOut = async () => {
    // Explicit logout must clear "Remember me" credentials, otherwise LoginPage
    // auto-signs the user back in immediately after RequireAuth redirects.
    clearRememberedCredentials();
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('⚠️ Supabase local signOut failed; removing storage directly', err);
    }
    if (supabaseStorageKey) {
      localStorage.removeItem(supabaseStorageKey);
    }
    setSession(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signUp,
        claimExistingUser,
        sendPasswordReset,
        sendPasswordOtp,
        verifyPasswordOtp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
